/**
 * Analytics Tracking System
 * Uniwersalne narzędzie do śledzenia zdarzeń w aplikacji
 * Centralizuje logikę śledzenia i zapewnia spójne API
 */

import { base44 } from '@/api/base44Client';

// ============================================
// UTILITY: Parsowanie i Zarządzanie Sesją
// ============================================

/**
 * Parsuje parametry UTM z URL-a
 * Zwraca obiekt ze źródłami ruchu
 */
export function parseUTMParameters() {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || 'direct',
    utm_medium: params.get('utm_medium') || 'organic',
    utm_campaign: params.get('utm_campaign') || null,
    utm_content: params.get('utm_content') || null,
    utm_term: params.get('utm_term') || null,
    referrer: document.referrer || 'direct',
  };
}

/**
 * Pobiera lub tworzy ID sesji (przechowywane w sessionStorage)
 */
export function getSessionId() {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
}

/**
 * Pobiera informacje o urządzeniu i przeglądarce
 */
export function getDeviceInfo() {
  const ua = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
  const isDesktop = !isMobile && !isTablet;

  let browser = 'Unknown';
  if (ua.includes('Chrome') && !ua.includes('Chromium')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edge')) browser = 'Edge';

  let os = 'Unknown';
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return {
    device_type: isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile',
    browser,
    os,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
  };
}

/**
 * Buduje bazowy obiekt właściwości dla każdego zdarzenia
 */
export function buildBaseEventProperties() {
  return {
    session_id: getSessionId(),
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    page_path: window.location.pathname,
    ...parseUTMParameters(),
    ...getDeviceInfo(),
  };
}

// ============================================
// ZDARZENIA: Page & Session
// ============================================

/**
 * Śledzenie wejścia na stronę
 * Można wywoływać z App.jsx w useEffect
 */
export function trackPageView() {
  base44.analytics.track({
    eventName: 'page_view',
    properties: {
      ...buildBaseEventProperties(),
    },
  });
}

/**
 * Śledzenie opuszczenia strony
 * Wywoływane z App.jsx w useEffect cleanup
 */
export function trackPageExit(timeSpentSeconds) {
  base44.analytics.track({
    eventName: 'page_exit',
    properties: {
      ...buildBaseEventProperties(),
      time_spent_seconds: timeSpentSeconds,
      exit_type: 'navigation',
    },
  });
}

// ============================================
// ZDARZENIA: Interakcje z Elementami
// ============================================

/**
 * Śledzenie kliknięcia w przycisk CTA
 * @param {string} buttonLabel - Tekst przycisku lub identyfikator
 * @param {string} buttonAction - Akcja, którą przycisk wykonuje (np. 'request_quote', 'download_pdf')
 * @param {object} additionalData - Dodatkowe dane do zdarzenia
 */
export function trackCTAClick(buttonLabel, buttonAction, additionalData = {}) {
  base44.analytics.track({
    eventName: 'cta_clicked',
    properties: {
      ...buildBaseEventProperties(),
      button_label: buttonLabel,
      button_action: buttonAction,
      section: additionalData.section || null,
      ...additionalData,
    },
  });
}

/**
 * Śledzenie kliknięcia w link
 * @param {string} linkText - Tekst linku
 * @param {string} linkHref - Docelowy URL
 * @param {string} linkType - Typ linku (internal, external, nav, footer, etc.)
 */
export function trackLinkClick(linkText, linkHref, linkType = 'internal') {
  base44.analytics.track({
    eventName: 'link_clicked',
    properties: {
      ...buildBaseEventProperties(),
      link_text: linkText,
      link_href: linkHref,
      link_type: linkType,
    },
  });
}

/**
 * Śledzenie interakcji z obrazem (kliknięcie, otworzenie w galerii)
 * @param {string} imageName - Nazwa lub identyfikator obrazu
 * @param {string} interactionType - Typ interakcji (view, click, zoom, lightbox_open)
 */
export function trackImageInteraction(imageName, interactionType = 'click') {
  base44.analytics.track({
    eventName: 'image_interaction',
    properties: {
      ...buildBaseEventProperties(),
      image_name: imageName,
      interaction_type: interactionType,
    },
  });
}

/**
 * Śledzenie rozpoczęcia wypełniania formularza
 * @param {string} formId - Identyfikator formularza
 * @param {string} formName - Nazwa formularza
 */
export function trackFormStarted(formId, formName) {
  base44.analytics.track({
    eventName: 'form_started',
    properties: {
      ...buildBaseEventProperties(),
      form_id: formId,
      form_name: formName,
    },
  });
}

/**
 * Śledzenie wysłania formularza
 * @param {string} formId - Identyfikator formularza
 * @param {string} formName - Nazwa formularza
 * @param {boolean} success - Czy wysłanie było udane
 * @param {object} formData - Dane z formularza (bez sensytywnych informacji!)
 */
export function trackFormSubmitted(formId, formName, success, formData = {}) {
  // Bezpieczeństwo: nie wysyłamy hasła, pełnych danych karty, etc.
  const safeFormData = { ...formData };
  delete safeFormData.password;
  delete safeFormData.card_number;
  delete safeFormData.cvv;

  base44.analytics.track({
    eventName: 'form_submitted',
    properties: {
      ...buildBaseEventProperties(),
      form_id: formId,
      form_name: formName,
      success,
      form_fields_count: Object.keys(formData).length,
      ...safeFormData,
    },
  });
}

// ============================================
// ZDARZENIA: Zaangażowanie w Treść
// ============================================

/**
 * Śledzenie głębokości przewijania strony
 * @param {number} percentage - Procent przewinięcia (0-100)
 */
export function trackScrollDepth(percentage) {
  base44.analytics.track({
    eventName: 'scroll_depth_reached',
    properties: {
      ...buildBaseEventProperties(),
      scroll_percentage: percentage,
    },
  });
}

/**
 * Śledzenie widoczności sekcji (Intersection Observer)
 * @param {string} sectionName - Nazwa sekcji
 */
export function trackSectionViewed(sectionName) {
  base44.analytics.track({
    eventName: 'section_viewed',
    properties: {
      ...buildBaseEventProperties(),
      section_name: sectionName,
    },
  });
}

/**
 * Śledzenie odtwarzania wideo
 * @param {string} videoId - Identyfikator wideo
 * @param {string} videoTitle - Tytuł wideo
 * @param {string} interactionType - Typ interakcji (play, pause, complete)
 */
export function trackVideoInteraction(videoId, videoTitle, interactionType = 'play') {
  base44.analytics.track({
    eventName: 'video_interaction',
    properties: {
      ...buildBaseEventProperties(),
      video_id: videoId,
      video_title: videoTitle,
      interaction_type: interactionType,
    },
  });
}

// ============================================
// ZDARZENIA: Konwersje i Cele Biznesowe
// ============================================

/**
 * Śledzenie wysłania zapytania ofertowego
 * @param {string} saunaModel - Model sauny
 * @param {number} price - Cena
 * @param {object} configDetails - Szczegóły konfiguracji (bez danych osobowych!)
 */
export function trackQuoteRequested(saunaModel, price, configDetails = {}) {
  base44.analytics.track({
    eventName: 'quote_requested',
    properties: {
      ...buildBaseEventProperties(),
      sauna_model: saunaModel,
      estimated_price: price,
      config_details_count: Object.keys(configDetails).length,
      conversion: true,
    },
  });
}

/**
 * Śledzenie pobrania dokumentu
 * @param {string} documentName - Nazwa dokumentu
 * @param {string} documentType - Typ (pdf, excel, image, etc.)
 * @param {string} documentUrl - URL dokumentu
 */
export function trackDocumentDownloaded(documentName, documentType, documentUrl) {
  base44.analytics.track({
    eventName: 'document_downloaded',
    properties: {
      ...buildBaseEventProperties(),
      document_name: documentName,
      document_type: documentType,
      document_url: documentUrl,
      conversion: true,
    },
  });
}

/**
 * Śledzenie kliknięcia na kontakt (telefon/email)
 * @param {string} contactType - Typ kontaktu (phone, email)
 * @param {string} contactValue - Wartość (numer/email)
 */
export function trackContactClicked(contactType, contactValue) {
  base44.analytics.track({
    eventName: 'contact_clicked',
    properties: {
      ...buildBaseEventProperties(),
      contact_type: contactType,
      // Nie wysyłamy pełnego kontaktu dla bezpieczeństwa
      contact_masked: contactValue.substring(0, 3) + '*'.repeat(contactValue.length - 6) + contactValue.substring(-3),
    },
  });
}

/**
 * Śledzenie akceptacji oferty przez klienta
 * @param {string} offerId - ID oferty
 * @param {number} offerValue - Wartość oferty
 */
export function trackOfferAccepted(offerId, offerValue) {
  base44.analytics.track({
    eventName: 'offer_accepted',
    properties: {
      ...buildBaseEventProperties(),
      offer_id: offerId,
      offer_value: offerValue,
      conversion: true,
    },
  });
}

/**
 * Śledzenie zalogowania się użytkownika
 * @param {string} userType - Typ użytkownika (admin, customer, guest)
 */
export function trackUserLogin(userType = 'customer') {
  base44.analytics.track({
    eventName: 'user_login',
    properties: {
      ...buildBaseEventProperties(),
      user_type: userType,
    },
  });
}

/**
 * Śledzenie wylogowania się użytkownika
 */
export function trackUserLogout() {
  base44.analytics.track({
    eventName: 'user_logout',
    properties: {
      ...buildBaseEventProperties(),
    },
  });
}

// ============================================
// ZDARZENIA: Konfigurator (Specifics)
// ============================================

/**
 * Śledzenie wyboru komponentu sauny w konfiguratorze
 * Już jest w ConfiguratorStepSlider, ale eksportujemy jako helper
 */
export function trackSaunaComponentSelected(
  componentId,
  componentName,
  optionId,
  optionLabel,
  optionPrice,
  stepNumber,
  totalSteps,
  isMultiselect = false
) {
  base44.analytics.track({
    eventName: 'sauna_component_selected',
    properties: {
      ...buildBaseEventProperties(),
      component_id: componentId,
      component_name: componentName,
      option_id: optionId,
      option_label: optionLabel,
      option_price: optionPrice,
      step_number: stepNumber,
      total_steps: totalSteps,
      is_multiselect: isMultiselect,
    },
  });
}

/**
 * Śledzenie ukończenia konfiguratora
 */
export function trackConfiguratorCompleted(totalPrice, saunaModel, deliveryType) {
  base44.analytics.track({
    eventName: 'configurator_completed',
    properties: {
      ...buildBaseEventProperties(),
      total_price: totalPrice,
      sauna_model: saunaModel,
      delivery_type: deliveryType,
      conversion: true,
    },
  });
}

export default {
  // Utility
  parseUTMParameters,
  getSessionId,
  getDeviceInfo,
  buildBaseEventProperties,
  // Page & Session
  trackPageView,
  trackPageExit,
  // Interactions
  trackCTAClick,
  trackLinkClick,
  trackImageInteraction,
  trackFormStarted,
  trackFormSubmitted,
  // Engagement
  trackScrollDepth,
  trackSectionViewed,
  trackVideoInteraction,
  // Conversions
  trackQuoteRequested,
  trackDocumentDownloaded,
  trackContactClicked,
  trackOfferAccepted,
  trackUserLogin,
  trackUserLogout,
  // Configurator
  trackSaunaComponentSelected,
  trackConfiguratorCompleted,
};